import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Save, Eye, EyeOff, Key, Youtube, Mail, Palette, CheckCircle, Play } from 'lucide-react';

const Settings = () => {
  const { API, fetchSettings } = useApp();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [settings, setSettings] = useState({
    youtube_api_key: '',
    youtube_channel_id: '',
    shorts_min_duration: 60,
    theme_settings: {
      default_theme: 'dark',
      scroll_reveal: true,
      hover_glow: true,
      accent_color_primary: '#7f1d1d',
      accent_color_secondary: '#b45309'
    },
    player_settings: {
      releases_embed: true,
      videos_embed: true
    },
    smtp_enabled: false,
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    setup_completed: false
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/admin/settings`, { withCredentials: true });
        setSettings(prev => ({ ...prev, ...response.data }));
      } catch (error) {
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [API]);

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleThemeChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      theme_settings: {
        ...prev.theme_settings,
        [field]: value
      }
    }));
  };

  const handlePlayerChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      player_settings: {
        ...prev.player_settings,
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, { withCredentials: true });
      await fetchSettings();
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteSetup = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/settings/complete-setup`, {}, { withCredentials: true });
      setSettings(prev => ({ ...prev, setup_completed: true }));
      await fetchSettings();
      toast.success('Setup completed! Your website is ready.');
    } catch (error) {
      toast.error('Failed to complete setup');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-syne font-bold text-2xl text-white">Settings</h1>
          <p className="text-white/50">Configure your website settings</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-brand-red hover:bg-brand-red-light"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>

      <Tabs defaultValue="youtube" className="space-y-6">
        <TabsList className="bg-brand-dark border border-white/5">
          <TabsTrigger value="youtube" className="data-[state=active]:bg-brand-red">
            <Youtube className="w-4 h-4 mr-2" />
            YouTube
          </TabsTrigger>
          <TabsTrigger value="player" className="data-[state=active]:bg-brand-red">
            <Play className="w-4 h-4 mr-2" />
            Player
          </TabsTrigger>
          <TabsTrigger value="theme" className="data-[state=active]:bg-brand-red">
            <Palette className="w-4 h-4 mr-2" />
            Theme
          </TabsTrigger>
          <TabsTrigger value="smtp" className="data-[state=active]:bg-brand-red">
            <Mail className="w-4 h-4 mr-2" />
            Email
          </TabsTrigger>
        </TabsList>

        {/* YouTube Settings */}
        <TabsContent value="youtube">
          <Card className="bg-brand-dark border-white/5">
            <CardHeader>
              <CardTitle className="font-syne text-white flex items-center gap-2">
                <Key className="w-5 h-5 text-brand-red" />
                YouTube API Configuration
              </CardTitle>
              <CardDescription className="text-white/50">
                Enter your YouTube Data API v3 credentials. Get your API key from{' '}
                <a 
                  href="https://console.cloud.google.com/apis/credentials" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-brand-red hover:underline"
                >
                  Google Cloud Console
                </a>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="youtube_api_key" className="text-white/70">API Key</Label>
                <div className="relative">
                  <Input
                    id="youtube_api_key"
                    type={showApiKey ? 'text' : 'password'}
                    value={settings.youtube_api_key || ''}
                    onChange={(e) => handleChange('youtube_api_key', e.target.value)}
                    className="bg-brand-charcoal border-white/10 text-white pr-10"
                    placeholder="AIza..."
                    data-testid="youtube-api-key"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="youtube_channel_id" className="text-white/70">Channel ID</Label>
                <Input
                  id="youtube_channel_id"
                  value={settings.youtube_channel_id || ''}
                  onChange={(e) => handleChange('youtube_channel_id', e.target.value)}
                  className="bg-brand-charcoal border-white/10 text-white"
                  placeholder="UC..."
                  data-testid="youtube-channel-id"
                />
                <p className="text-white/30 text-xs">
                  Find your Channel ID in YouTube Studio {'>'} Settings {'>'} Channel {'>'} Advanced settings
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="shorts_min_duration" className="text-white/70">
                  Shorts Filter (min duration in seconds)
                </Label>
                <Input
                  id="shorts_min_duration"
                  type="number"
                  min="30"
                  max="180"
                  value={settings.shorts_min_duration}
                  onChange={(e) => handleChange('shorts_min_duration', parseInt(e.target.value) || 60)}
                  className="bg-brand-charcoal border-white/10 text-white w-32"
                  data-testid="shorts-duration"
                />
                <p className="text-white/30 text-xs">
                  Videos shorter than this will be filtered out from the Music Videos page
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Player Settings */}
        <TabsContent value="player">
          <Card className="bg-brand-dark border-white/5">
            <CardHeader>
              <CardTitle className="font-syne text-white">Video Player Settings</CardTitle>
              <CardDescription className="text-white/50">
                Choose how videos are played on your website
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-white">Releases Player</Label>
                  <p className="text-white/30 text-sm">How releases are played when clicked</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant={settings.player_settings?.releases_embed !== false ? 'default' : 'outline'}
                    onClick={() => handlePlayerChange('releases_embed', true)}
                    className={settings.player_settings?.releases_embed !== false 
                      ? 'bg-brand-red' 
                      : 'border-white/10 text-white/70'}
                  >
                    Embedded
                  </Button>
                  <Button
                    variant={settings.player_settings?.releases_embed === false ? 'default' : 'outline'}
                    onClick={() => handlePlayerChange('releases_embed', false)}
                    className={settings.player_settings?.releases_embed === false 
                      ? 'bg-brand-red' 
                      : 'border-white/10 text-white/70'}
                  >
                    New Tab
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-white">Music Videos Player</Label>
                  <p className="text-white/30 text-sm">How music videos are played when clicked</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant={settings.player_settings?.videos_embed !== false ? 'default' : 'outline'}
                    onClick={() => handlePlayerChange('videos_embed', true)}
                    className={settings.player_settings?.videos_embed !== false 
                      ? 'bg-brand-red' 
                      : 'border-white/10 text-white/70'}
                  >
                    Embedded
                  </Button>
                  <Button
                    variant={settings.player_settings?.videos_embed === false ? 'default' : 'outline'}
                    onClick={() => handlePlayerChange('videos_embed', false)}
                    className={settings.player_settings?.videos_embed === false 
                      ? 'bg-brand-red' 
                      : 'border-white/10 text-white/70'}
                  >
                    New Tab
                  </Button>
                </div>
              </div>

              <div className="p-4 bg-brand-charcoal rounded-lg">
                <p className="text-white/50 text-sm">
                  <strong className="text-white">Embedded:</strong> Videos play in a modal overlay on your site (recommended for better user experience)<br/>
                  <strong className="text-white">New Tab:</strong> Videos open in a new YouTube tab
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Theme Settings */}
        <TabsContent value="theme">
          <Card className="bg-brand-dark border-white/5">
            <CardHeader>
              <CardTitle className="font-syne text-white">Theme & Effects</CardTitle>
              <CardDescription className="text-white/50">
                Customize the look and feel of your website
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-white">Default Theme</Label>
                  <p className="text-white/30 text-sm">Choose the default theme for visitors</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant={settings.theme_settings.default_theme === 'dark' ? 'default' : 'outline'}
                    onClick={() => handleThemeChange('default_theme', 'dark')}
                    className={settings.theme_settings.default_theme === 'dark' 
                      ? 'bg-brand-red' 
                      : 'border-white/10 text-white/70'}
                    data-testid="theme-dark"
                  >
                    Dark
                  </Button>
                  <Button
                    variant={settings.theme_settings.default_theme === 'light' ? 'default' : 'outline'}
                    onClick={() => handleThemeChange('default_theme', 'light')}
                    className={settings.theme_settings.default_theme === 'light' 
                      ? 'bg-brand-red' 
                      : 'border-white/10 text-white/70'}
                    data-testid="theme-light"
                  >
                    Light
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-white">Scroll Reveal Animations</Label>
                  <p className="text-white/30 text-sm">Enable fade-in animations on scroll</p>
                </div>
                <Switch
                  checked={settings.theme_settings.scroll_reveal}
                  onCheckedChange={(checked) => handleThemeChange('scroll_reveal', checked)}
                  data-testid="scroll-reveal-toggle"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-white">Hover Glow Effects</Label>
                  <p className="text-white/30 text-sm">Enable glow effects on interactive elements</p>
                </div>
                <Switch
                  checked={settings.theme_settings.hover_glow}
                  onCheckedChange={(checked) => handleThemeChange('hover_glow', checked)}
                  data-testid="hover-glow-toggle"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/70">Primary Accent</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={settings.theme_settings.accent_color_primary}
                      onChange={(e) => handleThemeChange('accent_color_primary', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={settings.theme_settings.accent_color_primary}
                      onChange={(e) => handleThemeChange('accent_color_primary', e.target.value)}
                      className="bg-brand-charcoal border-white/10 text-white font-mono"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">Secondary Accent</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={settings.theme_settings.accent_color_secondary}
                      onChange={(e) => handleThemeChange('accent_color_secondary', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={settings.theme_settings.accent_color_secondary}
                      onChange={(e) => handleThemeChange('accent_color_secondary', e.target.value)}
                      className="bg-brand-charcoal border-white/10 text-white font-mono"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMTP Settings */}
        <TabsContent value="smtp">
          <Card className="bg-brand-dark border-white/5">
            <CardHeader>
              <CardTitle className="font-syne text-white">Email Configuration</CardTitle>
              <CardDescription className="text-white/50">
                Configure SMTP for sending contact form emails (optional)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-white">Enable Email Sending</Label>
                  <p className="text-white/30 text-sm">Send contact form submissions via email</p>
                </div>
                <Switch
                  checked={settings.smtp_enabled}
                  onCheckedChange={(checked) => handleChange('smtp_enabled', checked)}
                  data-testid="smtp-enabled"
                />
              </div>

              {settings.smtp_enabled && (
                <div className="space-y-4 pt-4 border-t border-white/5">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white/70">SMTP Host</Label>
                      <Input
                        value={settings.smtp_host || ''}
                        onChange={(e) => handleChange('smtp_host', e.target.value)}
                        className="bg-brand-charcoal border-white/10 text-white"
                        placeholder="smtp.gmail.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/70">SMTP Port</Label>
                      <Input
                        type="number"
                        value={settings.smtp_port || 587}
                        onChange={(e) => handleChange('smtp_port', parseInt(e.target.value))}
                        className="bg-brand-charcoal border-white/10 text-white"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/70">SMTP Username</Label>
                    <Input
                      value={settings.smtp_user || ''}
                      onChange={(e) => handleChange('smtp_user', e.target.value)}
                      className="bg-brand-charcoal border-white/10 text-white"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/70">SMTP Password</Label>
                    <Input
                      type="password"
                      value={settings.smtp_password || ''}
                      onChange={(e) => handleChange('smtp_password', e.target.value)}
                      className="bg-brand-charcoal border-white/10 text-white"
                      placeholder="App password or SMTP password"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Complete Setup */}
      {!settings.setup_completed && (
        <Card className="bg-brand-red/10 border-brand-red/30">
          <CardContent className="flex items-center justify-between p-6">
            <div className="flex items-center gap-4">
              <CheckCircle className="w-8 h-8 text-brand-red" />
              <div>
                <h3 className="font-syne font-semibold text-white">Ready to Launch?</h3>
                <p className="text-white/60 text-sm">
                  Mark setup as complete when you're ready to show your website to the world.
                </p>
              </div>
            </div>
            <Button 
              onClick={handleCompleteSetup}
              disabled={saving}
              className="bg-brand-red hover:bg-brand-red-light"
              data-testid="complete-setup-btn"
            >
              Complete Setup
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Settings;
